import { useQuery } from "@tanstack/react-query";
import { Phone, Search, UserPlus } from "lucide-react";
import { useState } from "react";
import { get } from "../../api/client";
import type { Colleague, ColleagueStatus, Directory } from "../../api/types";
import { useCan } from "../../auth/session";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { EmptyState, ErrorState, Loading } from "../../components/ui/misc";

/** Colleagues and their extensions with live status (refreshes while in use). */
export function useDirectory(enabled = true) {
  const { can } = useCan();
  return useQuery({
    queryKey: ["voice", "directory"],
    queryFn: () => get<Directory>("/voice/directory"),
    enabled: enabled && can("calls.initiate"),
    staleTime: 10_000,
    refetchInterval: 20_000,
  });
}

/** The colleague whose extension is exactly what's been dialed (digits only), if any. */
export function matchExtension(directory: Directory | undefined, dialed: string): Colleague | null {
  const digits = dialed.replace(/[\s().-]/g, "");
  if (!/^\d{2,6}$/.test(digits)) return null;
  return directory?.items.find((c) => c.extension === digits) ?? null;
}

const STATUS: Record<ColleagueStatus, { label: string; dot: string }> = {
  available: { label: "Available", dot: "bg-emerald-500" },
  busy: { label: "On a call", dot: "bg-rose-500" },
  away: { label: "Away", dot: "bg-amber-500" },
  offline: { label: "Offline", dot: "bg-slate-400" },
};

export function StatusDot({ status }: { status: ColleagueStatus }) {
  return <span className={`inline-block h-2 w-2 rounded-full ${STATUS[status].dot}`} aria-hidden="true" />;
}
export const statusLabel = (s: ColleagueStatus) => STATUS[s].label;

/** Available first, then busy, away and offline; alphabetical within each group. */
const ORDER: Record<ColleagueStatus, number> = { available: 0, busy: 1, away: 2, offline: 3 };

/** Searchable grid of colleague cards (available first). Used to call a colleague and to add one to a call. */
export function ColleaguePicker({ directory, actionLabel, onPick, exclude = [], busyId }: {
  directory: Directory; actionLabel: string; onPick: (c: Colleague) => void; exclude?: string[]; busyId?: string | null;
}) {
  const [search, setSearch] = useState("");
  const term = search.trim().toLowerCase();
  const digits = term.replace(/\D/g, "");
  const matches = (c: Colleague) => !term || c.name.toLowerCase().includes(term) || (!!digits && c.extension.includes(digits));
  const items = directory.items
    .filter((c) => !exclude.includes(c.membershipId) && matches(c))
    .sort((a, b) => ORDER[a.status] - ORDER[b.status] || a.name.localeCompare(b.name));
  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" aria-hidden="true" />
        <label htmlFor="colleague-search" className="sr-only">Search by name or extension</label>
        <input
          id="colleague-search" type="search" autoComplete="off" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or extension"
          className="focus-ring h-10 w-full rounded-xl border border-white/70 bg-white/60 pl-9 pr-3 text-sm text-default placeholder:text-subtle"
        />
      </div>
      {items.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted" role="status">{term ? `No colleague matches “${search.trim()}”.` : "No other colleagues to show."}</p>
      ) : (
        // Only the grid scrolls; the scrollbar has its own gutter, clear of the cards.
        <ul className="scroll-soft -mr-3 grid max-h-[min(55vh,30rem)] grid-cols-2 gap-3 overflow-y-auto pb-1 pr-2 sm:grid-cols-3" aria-label="Colleagues">
          {items.map((c) => (
            <li key={c.membershipId} className={`flex min-w-0 flex-col items-center gap-2 rounded-2xl bg-white/55 p-4 text-center ring-1 ring-inset ring-white/70 transition ${c.status === "available" ? "hover:bg-white/85" : "opacity-80"}`}>
              <span className="relative">
                <span className="bg-accent-gradient grid h-12 w-12 place-items-center rounded-full text-base font-semibold text-white" aria-hidden="true">
                  {c.name.slice(0, 1).toUpperCase()}
                </span>
                <span className="absolute -bottom-0.5 -right-0.5 grid h-4 w-4 place-items-center rounded-full bg-white"><StatusDot status={c.status} /></span>
              </span>
              <span className="w-full min-w-0">
                <span className="block truncate text-sm font-semibold text-default" title={c.name}>{c.name}</span>
                <span className="block text-xs text-muted tabular">Ext. {c.extension}</span>
                <span className="block text-[11px] text-subtle">{statusLabel(c.status)}</span>
              </span>
              <Button size="sm" variant="success" className="mt-auto w-full" disabled={c.status !== "available" || !!busyId} loading={busyId === c.membershipId}
                onClick={() => onPick(c)} aria-label={`${actionLabel} ${c.name}`}>
                {actionLabel === "Call" ? <Phone className="h-4 w-4" aria-hidden="true" /> : <UserPlus className="h-4 w-4" aria-hidden="true" />} {actionLabel}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ColleaguesDialog({ open, onOpenChange, onCall }: { open: boolean; onOpenChange: (o: boolean) => void; onCall: (c: Colleague) => void }) {
  const q = useDirectory(open);
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Call a colleague" size="lg"
      description={q.data?.me.extension ? `Your extension is ${q.data.me.extension}. Calls between colleagues go browser to browser, without the phone network.` : "Calls between colleagues go browser to browser, without the phone network."}>
      {q.error ? <ErrorState error={q.error} /> : !q.data ? <Loading /> : q.data.items.length === 0 ? <EmptyState title="No colleagues yet" /> : (
        <ColleaguePicker directory={q.data} actionLabel="Call" onPick={onCall} />
      )}
    </Dialog>
  );
}
